import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import NavigateNextOutlined from "@mui/icons-material/NavigateNextOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useRouter } from "next/router";
import { keyframes, SxProps } from "@mui/material/styles";
import { useCustomerOrders } from "~/queries/use-customer-orders";
import { useCustomerSession } from "~/queries/use-customer-session";
import { PresenceSwipeableDrawer as SwipeableDrawer } from "~served/ui-comps/components/overlay-presence/presence-overlays";
import { orderDataHelper } from "~served/utils/order";
import { useGetCurrentDevice } from "~/queries/use-get-current-device";
import { DeviceFragmentFragment } from "~/types/__generated/gql/graphql";

export function ActiveOrdersModal({ onClose }) {
	const { data: device } = useGetCurrentDevice();
	const { customer } = useCustomerSession();

	return (
		<SwipeableDrawer
			open
			disableSwipeToOpen
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			onOpen={() => {}}
			anchor="bottom"
			onClose={onClose}
			sx={{
				".MuiPaper-root": {
					maxHeight: "95vh",
					width: "100%",
					borderRadius: (t) => t.spacing(4, 4, 0, 0),
				},
			}}>
			<Container
				maxWidth={false}
				component={Stack}
				py={{ xs: 2, sm: 3 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between" }}>
					<Typography variant="h6">
						{(device?._active_orders ?? []).length || "No"} active orders
					</Typography>
					<Fab
						size="small"
						sx={{
							zIndex: "unset",
						}}
						color="primary"
						onClick={onClose}>
						<CloseOutlined />
					</Fab>
				</Box>

				{!(device?._active_orders ?? []).length && (
					<Box
						width={1}
						textAlign="center">
						<img
							src="/images/order-success.png"
							width="200px"
							alt="all-prepared.png"
						/>
						<Typography
							variant="body2"
							color="text.secondary">
							You will see your processing orders here
						</Typography>
					</Box>
				)}

				{!!(device?._active_orders ?? []).length && (
					<Stack
						mt={{ xs: 2, sm: 3 }}
						gap={1}
						divider={<Divider sx={{ my: 1 }} />}>
						{(device?._active_orders ?? []).map((order) => (
							<OrderCard
								key={order._id}
								data={order}
								onClose={onClose}
							/>
						))}
					</Stack>
				)}
				{customer && (
					<PastOrders
						activeOrderIds={(device?._active_orders ?? []).map(
							(order) => order._id,
						)}
					/>
				)}
			</Container>
		</SwipeableDrawer>
	);
}

function PastOrders({ activeOrderIds }: { activeOrderIds: string[] }) {
	const { data, isLoading, isError, isFetching, refetch } = useCustomerOrders();
	const orders = data?.filter((order) => !activeOrderIds.includes(order._id));
	return (
		<Stack
			gap={2}
			pt={3}>
			<Typography variant="h6">Past orders</Typography>
			{isLoading && <CircularProgress aria-label="Loading past orders" />}
			{isError && (
				<Alert
					severity="error"
					action={
						<LoadingButton
							loading={isFetching}
							onClick={() => void refetch()}>
							Retry
						</LoadingButton>
					}>
					Unable to load past orders.
				</Alert>
			)}
			{orders?.length === 0 && (
				<Typography color="text.secondary">No past orders</Typography>
			)}
			{orders?.map((order) => (
				<Stack
					key={order._id}
					gap={1}>
					<Typography
						variant="caption"
						color="text.secondary">
						{dayjs(order.created_at).format("DD MMM YYYY, HH:mm")}
					</Typography>
					<OrderCard data={order} />
				</Stack>
			))}
		</Stack>
	);
}

function OrderCard({
	data,
	onClose,
}: {
	data: DeviceFragmentFragment["_active_orders"][number];
	onClose?: () => void;
}) {
	const { push } = useRouter();

	const { $d } = orderDataHelper(data as any);

	return (
		<Box
			display="flex"
			alignItems="center">
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					flexGrow: 1,
				}}>
				<Typography
					variant="body2"
					sx={{ "& span": { color: (t) => t.palette.text.secondary } }}>
					<b>{data._location?.name}</b> {"\u00B7"} <span>#{data.index}</span>
				</Typography>
				<Box
					display="flex"
					gap={1}>
					<Chip
						label={
							<Box
								fontSize={16}
								display="flex"
								alignItems="center"
								gap={0.5}>
								<Typography variant="caption">
									{$d(data.grand_total)}
								</Typography>
							</Box>
						}
					/>

					<Chip
						color="primary"
						component="span"
						label={data.status.charAt(0).toUpperCase() + data.status.slice(1)}
					/>

					{data.is_paid ? (
						<Chip
							color="success"
							component="span"
							label="Paid"
						/>
					) : (
						<Chip
							color="error"
							component="span"
							label="Unpaid"
						/>
					)}

					{data.status === "placed" &&
						!!data.is_needing_payment_confirmation &&
						(data.is_request_payment_confirmation ? (
							<Chip
								variant="outlined"
								color="primary"
								component="span"
								label="Awaiting payment confirmation"
								icon={<PulsatingDot sx={{ ml: 1 }} />}
							/>
						) : (
							<Chip
								variant="outlined"
								color="primary"
								component="span"
								label="Awaiting venue confirmation"
								icon={<PulsatingDot sx={{ ml: 1 }} />}
							/>
						))}

					{data.status === "placed" &&
						data.is_needing_prep_time_confirmation && (
							<Chip
								variant="outlined"
								color="primary"
								component="span"
								label="Awaiting prep time confirmation"
								icon={<PulsatingDot sx={{ ml: 1 }} />}
							/>
						)}

					{data.is_needing_prep_time_acknowledgement && (
						<Chip
							variant="outlined"
							color="primary"
							component="span"
							label="Prep time updated"
							icon={<PulsatingDot sx={{ ml: 1 }} />}
						/>
					)}
				</Box>
			</Box>
			{onClose && (
				<IconButton
					color="primary"
					onClick={() => {
						void push({ pathname: "/thank-you", query: { id: data._id } }).then(
							() => onClose(),
						);
					}}>
					<NavigateNextOutlined />
				</IconButton>
			)}
		</Box>
	);
}

const pulse = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.25);
    opacity: 1;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.6;
  }
`;

interface PulsatingDotProps {
	size?: number;
	color?: string;
	sx?: SxProps;
}

function PulsatingDot({
	size = 8,
	color = "primary.main",
	sx,
}: PulsatingDotProps) {
	return (
		<Box
			sx={{
				...sx,
				width: size,
				height: size,
				borderRadius: "50%",
				bgcolor: color,
				animation: `${pulse} 1.2s ease-in-out infinite`,
			}}
		/>
	);
}
